import { useState, useEffect, useRef, useCallback } from 'react';
import { notification } from 'tea-component';
import moment from 'moment';
import { useIntl } from 'umi';
import {
  getLifecycleFinancialForecast,
  editLifecycleFinancialForecast,
} from '@sg/services';
import { pickOperations } from '@sg/commonData/pickOperations';
import {
  createBtnPoint,
  SGBuriedBtnPoint,
  InDevProjectsTab,
} from '@/pages/strategic/utils/buriedPoint';
import {
  DefaultChat,
  showRevenueTrendList,
} from '../../../../../pageConfig';
import { buildLifecycleChartData } from '../utils/buildLifecycleChartData';
import { useLifecycleTableAutoSave } from './useLifecycleTableAutoSave';

export interface IUseLifecycleFinancialForecastParams {
  projectId: string;
  selectedMonth?: string;
  studioId?: string;
  projectInfo: string;
  onEditingChange?: (isEditing: boolean) => void;
}

export const useLifecycleFinancialForecast = ({
  projectId,
  selectedMonth,
  studioId,
  projectInfo,
  onEditingChange,
}: IUseLifecycleFinancialForecastParams) => {
  const { formatMessage } = useIntl();

  const [showChatsType, setShowChatsType] = useState<any>(DefaultChat);
  const [lifecycleUpdateTime, setLifecycleUpdateTime] = useState<string>('');
  const [lifecycleData, setLifecycleData] = useState<any>([]);
  const [lifecycleTableLoading, setLifecycleTableLoading] =
    useState<boolean>(false);
  const [originalLifecycleData, setOriginalLifecycleData] = useState<any[]>([]); // 保存原始数据用于对比
  const [copySalesForecast, setCopySalesForecast] = useState<any[]>([]);
  const [netRevenueForecast, setNetRevenueForecast] = useState<any[]>([]);
  const [allData, setAllData] = useState<any>({});

  // Lifecycle Financial Forecast 表格编辑状态
  const DCTRef = useRef<any>(null);
  const DCTOperationsRef = useRef<any>(null);
  const [isDCTEditing, setIsDCTEditing] = useState(false);

  // Auto-save hook for Lifecycle Financial Forecast table
  const {
    autoSaveVisible: lifecycleAutoSaveVisible,
    handleLifecycleEdit,
    handleAutoSaveCancel: handleLifecycleAutoSaveCancel,
    handleAutoSaveConfirm: handleLifecycleAutoSaveConfirm,
    clearLifecycleAutoSave,
    hasUnsavedChanges: hasLifecycleUnsavedChanges,
  } = useLifecycleTableAutoSave(
    isDCTEditing,
    lifecycleData,
    projectId,
    DCTRef,
    setLifecycleData,
  );

  const fetchLifecycleFinancialForecast = useCallback(async () => {
    try {
      setLifecycleTableLoading(true);
      const payload: { project_id: string; datatime?: string } = {
        project_id: projectId,
      };
      if (selectedMonth) {
        payload.datatime = selectedMonth;
      }
      const res: any = await getLifecycleFinancialForecast(payload);
      // const res: any = await getLifecycleFinancialForecast({ project_id: '802' }); // for demo purpose (to be removed)
      const latestUpdateTime = res?.data?.datatime;
      if (latestUpdateTime) {
        setLifecycleUpdateTime(moment(latestUpdateTime).format('YYYY-MM'));
      } else {
        setLifecycleUpdateTime('');
      }

      // Extract forecast data from API response
      setCopySalesForecast(res?.data?.copy_sales_forecast || []);
      setNetRevenueForecast(res?.data?.gross_revenue_forecast || []);

      // Transform API data to match the expected format
      const transformedData =
        res?.data?.data?.map((item: any) => ({
          date: item.year.toString(),
          'Dev Cost': item.dev_cost,
          'Mkt Cost': item.mkt_cost,
          'Other Cost': item.other_costs,
          'Total Project Cost': item.total_project_cost,
          Revenue: item.revenue,
          'Cum. Profit / Break even': item.cum_profit,
          revenue_is_actual: item.revenue_is_actual, // 1 = actual (normal), 0 = forecast (30% opacity)
          cost_is_actual: item.cost_is_actual, // 1 = actual (normal), 0 = forecast (30% opacity)
        })) || [];

      setLifecycleData(transformedData);

      // Store original data with IDs immediately after API fetch (before auto-save recovery)
      // IDs are needed because TableContent receives allData.table.data which has IDs added by setShowRevenueTrendList
      // When comparing editData (with IDs) against originalData, both must have the same structure
      // We set it here from API response to avoid auto-save recovery affecting the original baseline
      const dataWithId = transformedData.map((row: any) => ({
        ...row,
        id: row.date || `row-${Math.random()}`,
      }));
      setOriginalLifecycleData(JSON.parse(JSON.stringify(dataWithId)));
    } catch (error) {
      console.error('Error fetching lifecycle financial forecast:', error);
      notification.error({
        description: 'Failed to fetch lifecycle financial forecast data',
      });
      setLifecycleData([]);
      // Clear original data on error
      setOriginalLifecycleData([]);
    } finally {
      setLifecycleTableLoading(false);
    }
  }, [projectId, selectedMonth]);

  const updateLifecycleFinancialForecast = async (value: any) => {
    setLifecycleTableLoading(true);
    const currentYear = new Date().getFullYear();
    const metricsData = value
      .map((item: any) => {
        const {
          'Cum. Profit / Break even': cumProfitBreakEven,
          'Dev Cost': devCost,
          'Mkt Cost': mktCost,
          'Other Cost': otherCosts,
          Revenue: revenue,
          date,
        } = item;
        // 如果当前年份 >= date（处理条件）
        if (currentYear >= parseInt(date, 10)) {
          return null;
        }
        const formattedDate = `${date}-01-01`;
        return [
          {
            name: 'total dev costs forecast',
            value: devCost == null ? '' : String(devCost),
            date: formattedDate,
            date_type: 'year',
          },
          {
            name: 'marketing forecast',
            value: mktCost == null ? '' : String(mktCost),
            date: formattedDate,
            date_type: 'year',
          },
          {
            name: 'gross revenue forecast',
            value: revenue == null ? '' : String(revenue),
            date: formattedDate,
            date_type: 'year',
          },
          {
            name: 'cum profit forecast',
            value: cumProfitBreakEven == null ? '' : String(cumProfitBreakEven),
            date: formattedDate,
            date_type: 'year',
          },
          {
            name: 'other costs forecast',
            value: otherCosts == null ? '' : String(otherCosts),
            date: formattedDate,
            date_type: 'year',
          },
        ];
      })
      .flat() // 使用 flat() 展开数组，得到一个扁平的数组
      .filter((item: any) => item !== null && item !== undefined); // 过滤掉 null 或 undefined 的项
    const aipData: any = {
      project_id: Number(projectId) || '',
      studio_id: studioId || '',
      metrics: metricsData,
      datatime: lifecycleUpdateTime,
    };

    try {
      await editLifecycleFinancialForecast(aipData);
      await fetchLifecycleFinancialForecast();
      // Clear auto-save after successful save
      clearLifecycleAutoSave();
    } catch (error) {
      setLifecycleTableLoading(false);
      console.error('Error saving lifecycle financial forecast:', error);
      notification.error({
        description: 'Failed to save Lifecycle Financial Forecast. Please try again.',
      });
      throw error;
    }
  };

  useEffect(() => {
    if (projectId) {
      fetchLifecycleFinancialForecast();
    }
  }, [projectId, selectedMonth, fetchLifecycleFinancialForecast]);

  useEffect(() => {
    // Exit editing mode for Lifecycle Financial Forecast
    if (isDCTEditing) {
      setIsDCTEditing(false);
      clearLifecycleAutoSave();
      // Cancel any ongoing edits in the table component
      if (DCTRef.current?.handleCancel) {
        DCTRef.current.handleCancel();
      }
    }
    // Reset selected milestone when project changes (already handled in URL sync useEffect)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    const chartData = buildLifecycleChartData({
      data: lifecycleData,
      showChatsType,
      formatMessage,
      lifecycleUpdateTime,
      netRevenueForecast,
      copySalesForecast,
    });
    setAllData(chartData);
  }, [
    lifecycleData,
    showChatsType,
    formatMessage,
    lifecycleUpdateTime,
    netRevenueForecast,
    copySalesForecast,
  ]);

  useEffect(() => {
    onEditingChange?.(isDCTEditing);
  }, [isDCTEditing, onEditingChange]);

  // Handle lifecycle financial forecast chart type change with buried point tracking
  const handleLifecycleFinancialForecastChange = (
    e: any,
    downloadValue?: string,
  ) => {
    // Track buried point based on the selected value
    // For download, the second parameter is 'download' (only track when downloadValue is provided)
    // For view/bar/table, check e.value (but not when it's a download call)
    if (downloadValue === 'download') {
      // Only track download when the second parameter is explicitly 'download'
      // This prevents double tracking since RenderOperations calls change() twice for download
      const btnPoint = createBtnPoint(
        InDevProjectsTab,
        'lifecycleFinancialForecastDownload',
        `Lifecycle Financial Forecast - Download - ${projectInfo}`,
      );
      if (btnPoint) {
        SGBuriedBtnPoint(btnPoint);
      }
    } else if (e?.value && e.value !== 'download') {
      // Track view/bar/table only when e.value is set and it's not 'download'
      const value = e.value;

      // View button has isNoSelect: true and toggles format only.
      // When clicked, value remains unchanged while format changes.
      const isViewClick =
        e.format !== showChatsType?.format && value === showChatsType?.value;

      if (value === 'view' || isViewClick) {
        const btnPoint = createBtnPoint(
          InDevProjectsTab,
          'lifecycleFinancialForecastView',
          `Lifecycle Financial Forecast - View - ${projectInfo}`,
        );
        if (btnPoint) {
          SGBuriedBtnPoint(btnPoint);
        }
      } else if (value === 'bar') {
        // Only track if switching to bar (not already on bar)
        if (showChatsType?.value !== 'bar') {
          const btnPoint = createBtnPoint(
            InDevProjectsTab,
            'lifecycleFinancialForecastBarChart',
            `Lifecycle Financial Forecast - Bar Chart - ${projectInfo}`,
          );
          if (btnPoint) {
            SGBuriedBtnPoint(btnPoint);
          }
        }
      } else if (value === 'table') {
        // Only track if switching to table (not already on table)
        if (showChatsType?.value !== 'table') {
          const btnPoint = createBtnPoint(
            InDevProjectsTab,
            'lifecycleFinancialForecastTable',
            `Lifecycle Financial Forecast - Table - ${projectInfo}`,
          );
          if (btnPoint) {
            SGBuriedBtnPoint(btnPoint);
          }
        }
      }
    }

    // Only cancel table edits when actually leaving table edit mode.
    // Otherwise switching chart view can trigger unnecessary onCancelRefetch calls.
    if (showChatsType?.value === 'table' && isDCTEditing && e.value !== 'table') {
      clearLifecycleAutoSave();
      DCTRef.current?.handleCancel();
    }

    // Call the original setter
    setShowChatsType(e);
  };

  // 编辑状态变化回调
  const handleDCTEditingChange = (isEditing: boolean) => {
    // If exiting edit mode (cancel or save), clear auto-save
    // Note: Save already calls clearLifecycleAutoSave in updateLifecycleFinancialForecast
    // But cancel doesn't, so we clear it here when isDCTEditing changes from true to false
    if (isDCTEditing && !isEditing) {
      // User is exiting edit mode - clear auto-save
      clearLifecycleAutoSave();
    }
    setIsDCTEditing(isEditing);
  };

  // 处理编辑按钮点击 - 如果不是 table 视图，先切换到 table
  const handleDCTEditClick = () => {
    // Track buried point for Lifecycle Financial Forecast Edit button
    const btnPoint = createBtnPoint(
      InDevProjectsTab,
      'lifecycleFinancialForecastEdit',
      `Lifecycle Financial Forecast Edit - ${projectInfo}`,
    );
    if (btnPoint) {
      SGBuriedBtnPoint(btnPoint);
    }

    if (showChatsType?.value !== 'table') {
      const tableOption = pickOperations(
        [...showRevenueTrendList, 'download'],
        formatMessage,
      ).find((item: any) => item.value === 'table');
      if (tableOption) {
        setShowChatsType(tableOption);
        DCTOperationsRef.current?.setValue('table');
      }
      // 使用 setTimeout 确保视图切换完成后再进入编辑模式
      setTimeout(() => {
        handleLifecycleEdit(); // Use auto-save handler
      }, 200);
    } else {
      // 已经是 table 视图，直接进入编辑模式（with auto-save check)
      handleLifecycleEdit(); // Use auto-save handler
    }
  };

  return {
    showChatsType,
    lifecycleUpdateTime,
    lifecycleData,
    lifecycleTableLoading,
    originalLifecycleData,
    copySalesForecast,
    netRevenueForecast,
    allData,
    DCTRef,
    DCTOperationsRef,
    isDCTEditing,
    lifecycleAutoSaveVisible,
    handleLifecycleAutoSaveConfirm,
    handleLifecycleAutoSaveCancel,
    clearLifecycleAutoSave,
    hasLifecycleUnsavedChanges,
    handleLifecycleFinancialForecastChange,
    handleDCTEditingChange,
    handleDCTEditClick,
    updateLifecycleFinancialForecast,
    fetchLifecycleFinancialForecast,
    formatMessage,
  };
};
